"use client";

import { AuthError, isAuthApiError } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppNames } from "@watt/common/libs/supabase/cookie-config";
import { log } from "@watt/common/utils/axiom-logger";
import { queryKeysFactory } from "@watt/common/utils/query-keys-factory";
import { routes } from "@watt/crm/config/routes";
import { useTRPC } from "@watt/crm/trpc/client";
import { createBrowserClient } from "@watt/crm/utils/supabase/create-browser-client";
import { UserRole } from "@watt/db/enums/enums.generated";
import {
  FormControl,
  FormField,
  FormItem,
  FormWrapper
} from "@watt/ui/components/ui/form";
import { Verification } from "@watt/ui/components/verification/verification";
import { useZodForm } from "@watt/ui/hooks/use-zod-form";
import { useRouter } from "next/navigation";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { MagicLinkSchema } from "../../schema";
import {
  type EmailVerificationRef,
  EmailVerificationWrapper
} from "./email-verification-wrapper";

export function UserForgotPasswordForm() {
  const trpc = useTRPC();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isVerified, setIsVerified] = useState(false);
  const emailVerificationRef = useRef<EmailVerificationRef>(null);

  const form = useZodForm({
    defaultValues: {
      email: ""
    },
    mode: "onChange",
    schema: MagicLinkSchema
  });

  const getUserRoleByEmail = useQuery({
    ...trpc.userProfile.getUserRoleByEmail.queryOptions({
      email: form.getValues("email")
    }),
    enabled: false,
    retry: false
  });

  const handleClearEmailErrors = () => {
    form.clearErrors("email");
  };

  const handleEmailChange = (value: string) => {
    form.setValue("email", value);
    handleClearEmailErrors();
  };

  const handleSendVerificationCode = useCallback(
    async (email: string) => {
      const isEmailValid = await form.trigger("email");
      if (!isEmailValid) {
        throw new Error("Invalid email");
      }

      const { data } = await getUserRoleByEmail.refetch();

      if (!data) {
        throw new Error(
          "Something went wrong. Please check your email and try again."
        );
      }

      if (data?.role === UserRole.QUOTE_APP) {
        throw new Error("You are not authorised to access this application.");
      }

      const supabase = createBrowserClient();
      const error = await (async () => {
        try {
          const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
              shouldCreateUser: false
            }
          });

          return error;
        } catch (e) {
          log.error("Error sending verification code:", { error: e });
          return e;
        }
      })();

      if (error) {
        // Handle specific auth errors for sending OTP
        if (isAuthApiError(error)) {
          switch (error.code) {
            case "otp_disabled":
              throw new Error(
                "Email address not found. Please check your email and try again."
              );
            case "hook_timeout_after_retry":
              throw new Error(
                "Email verification is temporarily unavailable. Please try again later or contact support."
              );
            case "over_email_send_rate_limit":
              throw new Error(
                "Too many requests. Please wait before requesting a new verification code."
              );
            case "email_not_confirmed":
              throw new Error(
                "Email address not confirmed. Please check your email."
              );
            case "invalid_credentials":
              throw new Error("Invalid email address format.");
            case "unexpected_failure":
              // Check if it's the hook authorization error
              if (error.message === "Hook requires authorization token") {
                throw new Error(
                  "Email verification is temporarily unavailable. Please try again later or contact support."
                );
              }
              throw new Error(
                error.message || "Failed to send verification code"
              );
            default:
              throw new Error(
                error.message || "Failed to send verification code"
              );
          }
        }
        throw error;
      }

      toast.success("Verification code sent", {
        description: "Please check your email for the verification code."
      });

      return true;
    },
    [form, getUserRoleByEmail]
  );

  const handleVerifyCode = useCallback(
    async (email: string, code: string) => {
      const supabase = createBrowserClient();

      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: "email"
      });

      const { data } = await getUserRoleByEmail.refetch();

      if (error || data?.role === UserRole.QUOTE_APP) {
        const { title, description } = (() => {
          if (data?.role === UserRole.QUOTE_APP) {
            return {
              description: "You are not authorised to access this application.",
              title: "Unauthorised access"
            };
          }

          if (error instanceof AuthError) {
            // Handle specific auth error codes
            switch (error.code) {
              case "otp_expired":
                return {
                  description: "Please request a new verification code.",
                  title: "Verification code expired"
                };
              case "otp_disabled":
                return {
                  description: "Please contact support for assistance.",
                  title: "OTP verification disabled"
                };
              case "otp_not_found":
                return {
                  description: "Please check the code and try again.",
                  title: "Invalid verification code"
                };
              default:
                return {
                  description:
                    error.message || "Please check the code and try again.",
                  title: "Verification failed"
                };
            }
          }

          return {
            description: "Please check the code and try again.",
            title: "Verification failed"
          };
        })();

        toast.error(title, { description });

        return false;
      }

      toast.success("Email verified", {
        description: "Your email has been successfully verified."
      });

      queryClient.invalidateQueries({
        queryKey: queryKeysFactory.users.supabaseUser(AppNames.CRM)
      });
      router.push(routes.updatePassword);
      return true;
    },
    [queryClient, router, getUserRoleByEmail.refetch]
  );

  return (
    <FormWrapper form={form} handleSubmit={() => null}>
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Verification
                isVerified={isVerified}
                onChange={handleEmailChange}
                onSend={handleSendVerificationCode}
                onVerifiedChange={setIsVerified}
                onVerify={handleVerifyCode}
                resendDelay={60000}
                value={field.value}
              >
                <EmailVerificationWrapper
                  onClearErrors={handleClearEmailErrors}
                  ref={emailVerificationRef}
                />
              </Verification>
            </FormControl>
          </FormItem>
        )}
      />
    </FormWrapper>
  );
}
